var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore, } from "react";
/**
 * Certificates poller.
 *
 * A tiny external store that owns the "are the policy certificates ready yet?" state, so the
 * page does not have to keep it in React state. Only the component subscribed through
 * `useCertificatesPoller` re-renders when a tick changes something; the rest of the payment
 * confirmation screen (summary, email box, header) is untouched by the polling.
 */
export var CERTIFICATE_STATUS_PENDING = "PENDING";
export var DEFAULT_POLL_INTERVAL_MS = 4000;
export var DEFAULT_POLL_MAX_DURATION_MS = 60 * 1000;
var EMPTY_CERTIFICATES = [];
export var isGenerationPending = function (certificates) {
    return certificates.length === 0 ||
        certificates.some(function (certificate) { return certificate.status === CERTIFICATE_STATUS_PENDING; });
};
var sameCertificates = function (a, b) {
    return a.length === b.length &&
        a.every(function (item, index) {
            var _a, _b, _c, _d;
            var other = b[index];
            return (item.id === other.id &&
                item.name === other.name &&
                ((_a = item.url) !== null && _a !== void 0 ? _a : null) === ((_b = other.url) !== null && _b !== void 0 ? _b : null) &&
                ((_c = item.status) !== null && _c !== void 0 ? _c : null) === ((_d = other.status) !== null && _d !== void 0 ? _d : null));
        });
};
export function createCertificatesPoller(quoteId, options) {
    var _this = this;
    var _a, _b, _c;
    var intervalMs = (_a = options.intervalMs) !== null && _a !== void 0 ? _a : DEFAULT_POLL_INTERVAL_MS;
    var maxDurationMs = (_b = options.maxDurationMs) !== null && _b !== void 0 ? _b : DEFAULT_POLL_MAX_DURATION_MS;
    var now = (_c = options.now) !== null && _c !== void 0 ? _c : (function () { return Date.now(); });
    var listeners = new Set();
    var snapshot = {
        status: "idle",
        certificates: EMPTY_CERTIFICATES,
        attempts: 0,
        error: null,
    };
    var timer = null;
    var startedAt = null;
    var stopped = true;
    var fetched = false;
    var inFlight = 0;
    // Snapshots are immutable: a new object is published only when something changed, which is
    // what keeps useSyncExternalStore from re-rendering subscribers on every tick.
    var publish = function (next) {
        var _a, _b;
        var certificates = next.certificates &&
            !sameCertificates(next.certificates, snapshot.certificates)
            ? next.certificates
            : snapshot.certificates;
        var candidate = {
            status: (_a = next.status) !== null && _a !== void 0 ? _a : snapshot.status,
            certificates: certificates,
            attempts: (_b = next.attempts) !== null && _b !== void 0 ? _b : snapshot.attempts,
            error: next.error === undefined ? snapshot.error : next.error,
        };
        var changed = candidate.status !== snapshot.status ||
            candidate.certificates !== snapshot.certificates ||
            candidate.attempts !== snapshot.attempts ||
            candidate.error !== snapshot.error;
        if (!changed)
            return;
        snapshot = candidate;
        listeners.forEach(function (listener) { return listener(); });
    };
    var clearTimer = function () {
        if (timer !== null) {
            clearTimeout(timer);
            timer = null;
        }
    };
    var budgetExhausted = function () {
        return startedAt !== null && now() - startedAt >= maxDurationMs;
    };
    var scheduleTick = function () {
        clearTimer();
        if (stopped)
            return;
        if (budgetExhausted()) {
            publish({ status: "timeout" });
            return;
        }
        timer = setTimeout(function () {
            timer = null;
            void tick();
        }, intervalMs);
    };
    var settle = function (certificates) {
        fetched = true;
        if (isGenerationPending(certificates)) {
            publish({
                certificates: certificates,
                status: budgetExhausted() ? "timeout" : "generating",
                error: null,
            });
            scheduleTick();
            return;
        }
        clearTimer();
        publish({ certificates: certificates, status: "ready", error: null });
    };
    var tick = function () { return __awaiter(_this, void 0, void 0, function () {
        var requestId, certificates, error_1, message;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (stopped)
                        return [2 /*return*/];
                    requestId = ++inFlight;
                    if (snapshot.status === "idle") {
                        publish({ status: "loading" });
                    }
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, options.fetchCertificates(quoteId)];
                case 2:
                    certificates = _a.sent();
                    // A newer request (refresh) superseded this one: drop the stale result.
                    if (stopped || requestId !== inFlight)
                        return [2 /*return*/];
                    publish({ attempts: snapshot.attempts + 1 });
                    settle(certificates);
                    return [3 /*break*/, 4];
                case 3:
                    error_1 = _a.sent();
                    if (stopped || requestId !== inFlight)
                        return [2 /*return*/];
                    message = error_1 instanceof Error ? error_1.message : String(error_1);
                    console.log("[CertificatesPoller] fetch failed:", { quoteId: quoteId, message: message });
                    publish({
                        attempts: snapshot.attempts + 1,
                        status: budgetExhausted() ? "timeout" : "error",
                        error: message,
                    });
                    scheduleTick();
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); };
    return {
        subscribe: function (listener) {
            listeners.add(listener);
            return function () {
                listeners.delete(listener);
            };
        },
        getSnapshot: function () { return snapshot; },
        start: function (seed) {
            stopped = false;
            startedAt = now();
            if (seed && seed.length > 0) {
                if (isGenerationPending(seed)) {
                    publish({ certificates: seed, status: "generating" });
                    scheduleTick();
                }
                else {
                    publish({ certificates: seed, status: "ready" });
                }
                return;
            }
            void tick();
        },
        seed: function (certificates) {
            if (stopped || fetched || certificates.length === 0)
                return;
            if (isGenerationPending(certificates)) {
                publish({ certificates: certificates, status: "generating" });
                return;
            }
            clearTimer();
            publish({ certificates: certificates, status: "ready" });
        },
        refresh: function () {
            stopped = false;
            startedAt = now();
            clearTimer();
            publish({
                attempts: 0,
                status: snapshot.certificates.length === 0 ? "loading" : "generating",
                error: null,
            });
            void tick();
        },
        stop: function () {
            stopped = true;
            clearTimer();
        },
    };
}
var IDLE_SNAPSHOT = {
    status: "idle",
    certificates: EMPTY_CERTIFICATES,
    attempts: 0,
    error: null,
};
var noopSubscribe = function () { return function () { }; };
var getIdleSnapshot = function () { return IDLE_SNAPSHOT; };
/**
 * Subscribes ONLY the calling component to the certificates poller for `quoteId`.
 *
 * - `useSyncExternalStore` keeps React in sync with the store without React state in the page.
 * - `options` are read through a ref, so a new `fetchCertificates` identity never restarts polling.
 * - `seed` lets the page hand over the documents it already fetched, avoiding a duplicate request.
 */
export function useCertificatesPoller(quoteId, options, seed) {
    var _a, _b;
    var optionsRef = useRef(options);
    optionsRef.current = options;
    var intervalMs = (_a = options.intervalMs) !== null && _a !== void 0 ? _a : DEFAULT_POLL_INTERVAL_MS;
    var maxDurationMs = (_b = options.maxDurationMs) !== null && _b !== void 0 ? _b : DEFAULT_POLL_MAX_DURATION_MS;
    var poller = useMemo(function () {
        return quoteId
            ? createCertificatesPoller(quoteId, {
                intervalMs: intervalMs,
                maxDurationMs: maxDurationMs,
                now: optionsRef.current.now,
                fetchCertificates: function (id) { return optionsRef.current.fetchCertificates(id); },
            })
            : null;
    }, [quoteId, intervalMs, maxDurationMs]);
    var snapshot = useSyncExternalStore(poller ? poller.subscribe : noopSubscribe, poller ? poller.getSnapshot : getIdleSnapshot, poller ? poller.getSnapshot : getIdleSnapshot);
    var seedRef = useRef(seed);
    seedRef.current = seed;
    useEffect(function () {
        if (!poller)
            return undefined;
        poller.start(seedRef.current);
        return function () { return poller.stop(); };
    }, [poller]);
    useEffect(function () {
        if (poller && seed && seed.length > 0) {
            poller.seed(seed);
        }
    }, [poller, seed]);
    var refresh = useCallback(function () {
        poller === null || poller === void 0 ? void 0 : poller.refresh();
    }, [poller]);
    return __assign(__assign({}, snapshot), { isGenerating: snapshot.status === "loading" || snapshot.status === "generating", timedOut: snapshot.status === "timeout", refresh: refresh });
}
